import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, type User } from '../lib/api';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: { name: string; email: string; password: string }) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateProfile: (data: { name?: string; avatarUrl?: string | null }) => Promise<boolean>;
  /** Save the user's own OpenAI API key (BYOK). */
  setOpenAIKey: (key: string) => Promise<boolean>;
  /** Remove the user's own OpenAI API key, reverting to the platform key + quota. */
  clearOpenAIKey: () => Promise<boolean>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const { user } = await api.login(email, password);
          set({ user, isAuthenticated: true, isLoading: false });
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Login failed';
          set({ error: message, isLoading: false });
          return false;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const { user } = await api.register(data);
          set({ user, isAuthenticated: true, isLoading: false });
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Registration failed';
          set({ error: message, isLoading: false });
          return false;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await api.logout();
        } catch {
          // Ignore logout errors
        } finally {
          // Wipe biometric credentials so a logged-out device can't unlock.
          try {
            const { disableBiometric } = await import('../lib/biometric');
            await disableBiometric();
          } catch {
            /* ignore */
          }
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const { user } = await api.getMe();
          set({
            user,
            isAuthenticated: !!user,
            isLoading: false
          });
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      },

      updateProfile: async (data) => {
        try {
          await api.updateProfile(data);
          // Merge updates into the current user object in the store
          set((state) => ({
            user: state.user ? { ...state.user, ...data } : state.user,
          }));
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to update profile';
          set({ error: message });
          return false;
        }
      },

      setOpenAIKey: async (key) => {
        try {
          const res = await api.setOpenAIKey(key);
          set((state) => ({
            user: state.user
              ? { ...state.user, hasOwnKey: res.hasOwnKey, keyMaskedSuffix: res.keyMaskedSuffix }
              : state.user,
          }));
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Falha ao salvar chave';
          set({ error: message });
          return false;
        }
      },

      clearOpenAIKey: async () => {
        try {
          await api.clearOpenAIKey();
          set((state) => ({
            user: state.user
              ? { ...state.user, hasOwnKey: false, keyMaskedSuffix: undefined }
              : state.user,
          }));
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Falha ao remover chave';
          set({ error: message });
          return false;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'articulat-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
